import AutoSizer from "react-virtualized-auto-sizer";
import YAxisTick from "@common/components/YAxisTick";
import XAxisTick from "@common/components/XAxisTick";
import ChartTooltip from "@common/components/ChartTooltip";
import React, { FC } from "react";
import { ScenarioID } from "@modules/mgp-sso/types";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";
import { currencyFormatter, last2DigitYearFormatter } from "@common/utils";
import {
  useFinancialPlanDates,
  useProjectedFinancialPlanChartData,
} from "@modules/financialplan/hooks";
import moment from "moment";
import LoadingIndicator from "@common/components/LoadingIndicator";
import { themeColors, themeBorderColors } from "@common/constants";
import { Typography } from "@earned/core";

export interface PlanOverviewChart {
  name: number | string;
  recommended: number;
  agesOrEvents: string | null;
}

const ProjectedFinancialPlanAreaChart: FC = () => {
  const { retirementYear, planningYear, planMostRecentDate } =
    useFinancialPlanDates();

  const chartData = useProjectedFinancialPlanChartData();
  return (
    <>
      <LoadingIndicator isVisible={chartData.length === 0} />
      {chartData.length !== 0 && (
        <AutoSizer>
          {({ width, height }: { width: number; height: number }) => (
            <AreaChart
              width={width}
              height={height}
              data={chartData}
              margin={{ left: -22, bottom: 10, top: 30 }}
            >
              <XAxis
                dx={-14}
                padding={{ left: 30 }}
                dataKey="date"
                minTickGap={-35}
                type="number"
                domain={[chartData[0].date, chartData.at(-1)?.date ?? 0]}
                scale="time"
                tickLine={false}
                tick={XAxisTick}
                textAnchor="end"
                tickFormatter={(value) => {
                  const valueYear = moment(value).format("YYYY");
                  if (
                    moment(planMostRecentDate).format("YYYY-MM-DD") ===
                    moment(value).format("YYYY-MM-DD")
                  ) {
                    return `Today`;
                  } else if (valueYear === retirementYear.toString()) {
                    return `Retirement`;
                  } else if (valueYear === (planningYear + 1).toString()) {
                    return width > 700
                      ? valueYear
                      : last2DigitYearFormatter(parseInt(valueYear));
                  } else {
                    return "";
                  }
                }}
              />
              <YAxis
                className="text-left"
                axisLine={false}
                tickLine={false}
                tick={YAxisTick}
                tickFormatter={(value) =>
                  `${currencyFormatter(value, 0, true)}`
                }
              />
              <CartesianGrid
                strokeDasharray="0"
                stroke={themeBorderColors.primary}
                vertical={false}
              />
              <Tooltip
                content={(props: TooltipProps<ValueType, NameType>) => {
                  const [itemA] = props?.payload || [];
                  const isToday =
                    moment().format("YYYY-MM-DD") ===
                    moment(props.label).format("YYYY-MM-DD");
                  if (!itemA) return null;
                  return (
                    <ChartTooltip
                      title={
                        <div className="flex flex-row space-x-2.5">
                          <Typography
                            variant="body"
                            color="primary"
                            weight="medium"
                          >
                            {isToday
                              ? "Today"
                              : moment(props.label).format("MMM YYYY")}{" "}
                          </Typography>
                          {itemA.payload.agesOrEvents && (
                            <Typography variant="body" color="primary">
                              {itemA.payload.agesOrEvents}
                            </Typography>
                          )}
                        </div>
                      }
                    >
                      <div className="mb-2 flex h-4 flex-row items-center justify-between space-x-10">
                        <div className="flex flex-row items-center">
                          <div
                            className="mr-2 h-3 w-3 rounded-full"
                            style={{
                              background: itemA.color,
                              opacity: "90%",
                            }}
                          />
                          <Typography variant="body" color="primary">
                            With Earned Plan
                          </Typography>
                        </div>
                        <Typography variant="body" color="secondary">
                          {currencyFormatter(itemA.value as number)}
                        </Typography>
                      </div>
                    </ChartTooltip>
                  );
                }}
              />
              <Legend
                iconType="circle"
                iconSize={10}
                align="left"
                content={(props) => {
                  const { payload } = props;
                  return (
                    <div className="ml-5 mt-5 flex flex-row items-center pl-0.5">
                      {payload?.map((entry, index) => (
                        <div
                          key={`item-${index}`}
                          className="mr-4 flex flex-row items-center"
                        >
                          <div
                            className="mr-2 h-3 w-3 flex-none rounded-full"
                            style={{
                              backgroundColor: entry.color,
                              opacity: "90%",
                            }}
                          />
                          <Typography variant="body-sm" color="primary">
                            With Earned Plan
                          </Typography>
                        </div>
                      ))}
                    </div>
                  );
                }}
                verticalAlign="bottom"
                height={36}
              />
              <Area
                dataKey={ScenarioID.Recommended}
                stroke="none"
                fillOpacity={0.9}
                fill={themeColors.green[5]}
              />
            </AreaChart>
          )}
        </AutoSizer>
      )}
    </>
  );
};

export default ProjectedFinancialPlanAreaChart;
